import { AccordionSummary, Collapse, Typography } from '@material-ui/core';
import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { UrlParams } from 'src/modules/shared/types';
import {
  useGetTableOfContentChaptersQuery,
  useGetTableOfContentQuery,
} from 'src/store/table-of-contents/tableOfContentsApi';
import {
  TableOfContentChildDetails,
  TableOfContentsChapter,
} from 'src/store/table-of-contents/types';
import {
  TrajectoryItemAccordion,
  TrajectoryItemAccordionDetails,
  AccordionItemWrapper,
} from './TrajectoryAccordionStyled';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { TrajectoryAccordionItem } from './SubChapterTrajectoryItem/SubChapterTrajectoryItem';
import { DraftTrajectoryPopover } from './DraftTrajectoryPopover/DraftTrajectoryPopover';
import { AccordionSummaryContent } from './SubChapterTrajectoryItem/SubChapterTrajectoryItemStyed';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { scrollToViewById } from 'src/modules/shared/helpers/scroll';
import { setChapterToRemind } from 'src/store/table-of-contents/tableOfContentsSlice';

export const TrajectoryAccordion: FC<{
  chapter: TableOfContentsChapter;
  setIsDisabled: Dispatch<SetStateAction<boolean>>;
}> = ({ chapter, setIsDisabled }) => {
  let history = useHistory();
  const dispatch = useAppDispatch();
  let params = useParams<UrlParams>();
  const { bookId } = params;
  const expandedIndex = useAppSelector((state) => state.tableOfContents.expandedIndex);
  const { data: chapters } = useGetTableOfContentQuery(bookId ?? '', { skip: !bookId });
  const { data, isSuccess, isLoading } = useGetTableOfContentChaptersQuery(
    { chapterId: chapter.id },
    {
      skip: !bookId || !chapter.id || expandedIndex !== chapter.id,
      refetchOnMountOrArgChange: true,
    }
  );

  const [isTrajectoryExpanded, setIsTrajectoryExpanded] = useState<string[]>([]);
  const [openIsDraftPopover, setOpenIsDraftPopover] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const color = chapters?.chapters.find((colorItem) => colorItem.id === chapter.id)?.color ?? 'red';

  useEffect(() => {
    if (isSuccess && data && data) {
      if (data.chapters) {
        const ids = data.chapters.map((item) => item.id);
        setIsTrajectoryExpanded(ids);
      } else if (data.trajectories) {
        const ids = data.trajectories.map((item) => item.id);
        setIsTrajectoryExpanded(ids);
      }

      setTimeout(() => {
        scrollToViewById(`chapter-${chapter.id}`);
      }, 500);
    }
  }, [chapter.id, data, isSuccess]);

  useEffect(() => {
    setIsDisabled(isLoading);
  }, [isLoading, setIsDisabled]);

  const handleExpand = useCallback(
    (id: string) => {
      if (!data) return;
      setIsTrajectoryExpanded((prev) => {
        if (!!prev.find((item) => item === id)) {
          return prev.filter((item) => item !== id);
        } else {
          return [...prev, id];
        }
      });
    },
    [data]
  );

  const handleTrajectoryClick = useCallback(
    (chapterId: string, trajectory: TableOfContentChildDetails) => {
      if (trajectory.isDraft) {
        const element = document.getElementById(trajectory.id);
        if (element) {
          setAnchorEl(element);
        }
        setOpenIsDraftPopover(true);
      } else {
        dispatch(setChapterToRemind(chapter.id));
        history.push(
          `/nl-be/boekentas/${bookId}/hoofdstukken/${chapterId}/traject/${trajectory.id}/0/opbouw/`,
          {
            chapterId,
            trajectoryId: trajectory.id,
            bookId,
          }
        );
      }
    },
    [bookId, history, chapter, dispatch]
  );

  return (
    <div>
      {data &&
        data?.chapters &&
        data?.chapters.map((chapterDetail) => {
          return (
            <Collapse in={expandedIndex === chapter.id} key={chapterDetail.id}>
              <TrajectoryItemAccordion
                square
                expanded={!!isTrajectoryExpanded.find((item) => item === chapterDetail.id)}
              >
                <AccordionSummary
                  onClick={() => handleExpand(chapterDetail.id)}
                  expandIcon={
                    <FontAwesomeSpriteIcon
                      icon={SpriteIconsLight.CHEVRON_DOWN}
                      iconSize="xs"
                      color={'#000000'}
                    />
                  }
                  aria-controls="panel1d-content"
                  id="panel1d-header"
                  style={{ backgroundColor: `${color}0D` }}
                >
                  <AccordionSummaryContent>
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                      {chapterDetail.number}
                    </Typography>
                    <Typography style={{ fontWeight: 600 }}>{chapterDetail.title}</Typography>
                  </AccordionSummaryContent>
                </AccordionSummary>

                <TrajectoryItemAccordionDetails>
                  {chapterDetail.subChapters?.map((subChapterItem) => {
                    return (
                      <>
                        <TrajectoryAccordionItem
                          item={subChapterItem}
                          type="subchapter"
                          key={subChapterItem.id}
                        />
                        {subChapterItem.trajectories?.map((trajectoryItem) => {
                          return (
                            <AccordionItemWrapper
                              key={trajectoryItem.id}
                              color={chapter.color}
                              onClick={() =>
                                handleTrajectoryClick(subChapterItem.id, trajectoryItem)
                              }
                            >
                              <TrajectoryAccordionItem
                                item={trajectoryItem}
                                type="child-trajectory"
                                parent={chapter}
                              />
                            </AccordionItemWrapper>
                          );
                        })}
                      </>
                    );
                  })}
                  {chapterDetail.trajectories?.map((trajectoryItem) => {
                    return (
                      <AccordionItemWrapper
                        key={trajectoryItem.id}
                        color={chapter.color}
                        onClick={() => handleTrajectoryClick(chapterDetail.id, trajectoryItem)}
                      >
                        <TrajectoryAccordionItem
                          item={trajectoryItem}
                          type="trajectory"
                          parent={chapter}
                        />
                      </AccordionItemWrapper>
                    );
                  })}
                </TrajectoryItemAccordionDetails>
              </TrajectoryItemAccordion>
            </Collapse>
          );
        })}

      {data &&
        data.trajectories?.map((trajectoryItem) => {
          return (
            <Collapse in={expandedIndex === chapter.id} key={trajectoryItem.id}>
              <AccordionItemWrapper
                key={trajectoryItem.id}
                color={chapter.color}
                onClick={() => handleTrajectoryClick(chapter.id, trajectoryItem)}
              >
                <TrajectoryAccordionItem item={trajectoryItem} type="trajectory" parent={chapter} />
              </AccordionItemWrapper>
            </Collapse>
          );
        })}

      {openIsDraftPopover && anchorEl && (
        <DraftTrajectoryPopover
          open={openIsDraftPopover}
          setOpen={setOpenIsDraftPopover}
          anchorEl={anchorEl}
        />
      )}
    </div>
  );
};
